import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

/**
 * Companies the signed-in user's company may start a message with, plus the
 * people at each one. Eligibility is resolved server-side by
 * `list_message_counterparties()` (menu access in either direction, or a prior
 * conversation) — the client never supplies a company id.
 */
export interface CounterpartyPerson {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  company_name: string | null;
  is_primary_contact: boolean;
}

export interface MessageCounterparty {
  company_id: string;
  company_name: string | null;
  is_provisional: boolean;
  auto_generated_name: boolean;
  primary_contact_user_id: string | null;
  users: CounterpartyPerson[];
}

export function useMessageCounterparties(enabled = true) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['message-counterparties', user?.id],
    enabled: enabled && !!user,
    staleTime: 60_000,
    queryFn: async (): Promise<MessageCounterparty[]> => {
      const { data, error } = await supabase.rpc('list_message_counterparties');
      if (error) throw error;
      return (data as unknown as MessageCounterparty[]) ?? [];
    },
  });
}
